import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ withCredentials: true });

export default function ServicesEditor() {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    http.get(`${API}/admin/services`)
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Erro ao carregar serviços."));

  useEffect(() => { load(); }, []);

  const update = (i, field, value) =>
    setItems((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));

  const add = () =>
    setItems((prev) => [...prev, { id: null, name: "", price: 0, duration: 30, active: true }]);

  const remove = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    if (items.some((s) => s.name.trim().length < 2)) {
      toast.error("Todo serviço precisa de um nome.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await http.put(`${API}/admin/services`, { items });
      setItems(data);
      toast.success("Serviços salvos. O site já mostra os novos valores.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  if (!items) {
    return <p className="py-16 text-center text-sm text-smoke" data-testid="services-loading">Carregando...</p>;
  }

  return (
    <main className="max-w-6xl mx-auto px-5 md:px-8 py-10" data-testid="services-editor">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-bebas text-4xl tracking-wide text-cream">Serviços e preços</h2>
          <p className="text-sm text-smoke mt-1">Serviços desativados somem do agendamento, mas o histórico é mantido.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={add}
            data-testid="services-add-button"
            className="focus-gold inline-flex items-center gap-2 border border-gold text-gold font-bebas text-lg tracking-widest px-5 py-2.5 hover:bg-gold hover:text-ink transition-colors"
          >
            <Plus className="w-4 h-4" /> Novo serviço
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="services-save-button"
            className="focus-gold inline-flex items-center gap-2 bg-gold text-ink font-bebas text-lg tracking-widest px-6 py-2.5 hover:bg-golddark transition-colors disabled:opacity-40"
          >
            <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>

      <div className="mt-8 space-y-3">
        {items.map((s, i) => (
          <div
            key={i}
            data-testid={`service-row-${i}`}
            className={`grid grid-cols-2 md:grid-cols-[1fr_130px_130px_110px_48px] gap-3 items-center border p-4 transition-colors ${s.active ? "border-white/10 bg-surface" : "border-white/5 bg-surface/40 opacity-60"}`}
          >
            <input
              type="text"
              value={s.name}
              onChange={(e) => update(i, "name", e.target.value)}
              placeholder="Nome do serviço"
              aria-label={`Nome do serviço ${i + 1}`}
              data-testid={`service-name-${i}`}
              className="focus-gold col-span-2 md:col-span-1 bg-ink border border-white/15 text-cream font-manrope px-4 py-2.5 text-sm outline-none"
            />
            <label className="flex items-center gap-2 bg-ink border border-white/15 px-3 py-2.5">
              <span className="text-xs text-smoke">R$</span>
              <input
                type="number"
                min="0"
                step="1"
                value={s.price}
                onChange={(e) => update(i, "price", Number(e.target.value))}
                aria-label={`Preço do serviço ${i + 1}`}
                data-testid={`service-price-${i}`}
                className="focus-gold w-full bg-transparent text-cream font-manrope text-sm outline-none"
              />
            </label>
            <label className="flex items-center gap-2 bg-ink border border-white/15 px-3 py-2.5">
              <input
                type="number"
                min="5"
                max="240"
                step="5"
                value={s.duration}
                onChange={(e) => update(i, "duration", Number(e.target.value))}
                aria-label={`Duração do serviço ${i + 1}`}
                data-testid={`service-duration-${i}`}
                className="focus-gold w-full bg-transparent text-cream font-manrope text-sm outline-none"
              />
              <span className="text-xs text-smoke">min</span>
            </label>
            <button
              onClick={() => update(i, "active", !s.active)}
              data-testid={`service-active-${i}`}
              className={`focus-gold font-bebas tracking-widest text-sm px-3 py-2.5 border transition-colors ${s.active ? "border-eragreen text-green-400" : "border-white/15 text-smoke"}`}
            >
              {s.active ? "Ativo" : "Inativo"}
            </button>
            <button
              onClick={() => remove(i)}
              aria-label={`Remover serviço ${i + 1}`}
              data-testid={`service-remove-${i}`}
              className="focus-gold p-2.5 border border-white/15 text-smoke hover:border-red-500 hover:text-red-400 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <p className="mt-6 text-xs text-smoke">Lembre-se de clicar em <span className="text-gold">Salvar</span> para aplicar as mudanças no site.</p>
    </main>
  );
}
